/**
 * @file BillStatusDialog.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-15
 *
 * A bill's status and Queried, and nothing else (Cam, 5 October 2026).
 * PATCH /api/bills/:id/status. On a bill entered here that is already in
 * Xero this is the one change the system makes there, and it says beforehand
 * what Xero will do:
 *   - Authorised: a draft is approved in Xero too;
 *   - Queried on an approved bill: voided in Xero and sent again as a draft
 *     (refused with payments against it, or on a locked date);
 *   - Queried on a draft: stays a draft;
 *   - Draft on an approved bill (not queried): Xero cannot do it, so Save is
 *     disabled and the reason shown.
 * Xero first: a refusal changes nothing, and its reason is shown here.
 *
 * 1.1.0 (2026-10-05, Cam): ticking Queried on a bill approved in Xero asks
 * first ("This bill is approved in Xero. Marking it queried will void it in
 * Xero and send it again as a Draft. Continue?"); No leaves it unticked.
 * Unticking Queried puts the status back to Authorised.
 *
 * 1.1.1 (2026-10-05): statuses shown as Draft / Approved / Paid
 * (utils/billStatus.billStatusLabel) — the words only; values unchanged.
 *
 * 1.2.0 (2026-10-15, Cam): a bill in Xero dated in a month that should be
 * locked by now is asked about (usePriorMonthConfirm) before its status
 * changes.
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Dropdown from './Dropdown';
import ConfirmDialog from './ConfirmDialog';
import usePriorMonthConfirm from '../hooks/usePriorMonthConfirm';
import { useSlideOutButton } from './SlideOutButton';
import { billInXero, billStatusOutcome, billStatusLabel, BILL_STATUS_CHOICES } from '../utils/billStatus';
import './BillStatusDialog.css';

const STATUS_OPTIONS = BILL_STATUS_CHOICES;

function BillStatusBody({ bill, status, setStatus, queried, onQueriedChange, outcome, error, saving }) {
  useSlideOutButton([saving, outcome.blocked]);
  const inXero = billInXero(bill);
  return (
    <div className="bill-status-body">
      <p className="bill-status-now">
        Now: <strong>{billStatusLabel(bill.status)}</strong>{bill.is_queried ? ' · Queried' : ''}
        {' · '}{inXero ? 'in Xero' : 'not in Xero yet'}
      </p>
      <div className="bill-status-fields">
        <div className="form-group">
          <label>Status</label>
          <Dropdown
            value={queried ? 'Draft' : status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            disabled={queried || saving}
          />
        </div>
        <div className="form-group">
          <label>Queried</label>
          <div className="checkbox-container">
            <div className="check light">
              <input
                id="bill-status-queried"
                type="checkbox"
                checked={queried}
                disabled={saving}
                onChange={(e) => onQueriedChange(e.target.checked)}
              />
              <label htmlFor="bill-status-queried"></label>
            </div>
          </div>
        </div>
      </div>
      {queried && <p className="bill-status-hint">A queried bill is always a draft, here and in Xero.</p>}
      {outcome.text && (
        <div className={`bill-status-outcome ${outcome.blocked ? 'blocked' : ''}`}>
          <span className="material-symbols-rounded">{outcome.blocked ? 'block' : 'info'}</span>
          <span>{outcome.text}</span>
        </div>
      )}
      {error && (
        <div className="bill-status-outcome blocked" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.bill — id, status, is_queried, source, xero_bill_id, supplier_reference
 * @param {function} props.onClose
 * @param {function} props.onChanged — (response) after a change was saved
 */
function BillStatusDialog({ bill, onClose, onChanged }) {
  const [status, setStatus] = useState(bill?.is_queried ? 'Authorised' : (bill?.status === 'Draft' ? 'Draft' : 'Authorised'));
  const [queried, setQueried] = useState(!!bill?.is_queried);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Asking before a bill approved in Xero is queried (Xero voids it).
  const [confirmQuery, setConfirmQuery] = useState(false);
  // The status shown before Queried was ticked, to go back to when it is
  // cleared (a queried bill's own status is Authorised — Cam's rule).
  const [statusBeforeQuery, setStatusBeforeQuery] = useState(null);
  // A bill in Xero dated in a month that should be locked by now: asked
  // (Cam, 15 Oct 2026); Save anyway sends it again, Go back changes nothing.
  const priorMonth = usePriorMonthConfirm();

  if (!bill) return null;
  const outcome = billStatusOutcome(bill, { status: queried ? 'Draft' : status, queried });
  const approvedInXero = billInXero(bill) && String(bill.status || '').toLowerCase() === 'authorised' && !bill.is_queried;
  const tickQueried = () => { setStatusBeforeQuery(status); setQueried(true); };
  const onQueriedChange = (on) => {
    if (on && approvedInXero) { setConfirmQuery(true); return; }
    if (on) { tickQueried(); return; }
    setQueried(false);
    // Cleared: back to the status shown before it was ticked, else Authorised.
    setStatus(statusBeforeQuery || 'Authorised');
    setStatusBeforeQuery(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const sent = await priorMonth.run((extra) => fetch(`${API_URL}/api/bills/${bill.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: queried ? 'Draft' : status, is_queried: queried, ...extra }),
      }));
      if (sent.declined) return;
      const { res } = sent;
      const data = sent.data || {};
      if (!res.ok || !data.success) {
        setError(data.error || 'The status could not be changed.');
        return;
      }
      onChanged && onChanged(data);
      onClose && onClose();
    } catch (err) {
      setError(`The status could not be changed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const ref = bill.supplier_reference || bill.bill_number || `#${bill.id}`;
  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title={`Status — bill ${ref}`}
      size="sm"
      className="bill-status"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button
              className="btn-slideout"
              onClick={save}
              disabled={saving || outcome.blocked || outcome.unchanged}
              title={outcome.blocked ? outcome.text : (outcome.unchanged ? 'Nothing has changed' : 'Save')}
            >
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>{saving ? 'progress_activity' : 'save'}</span>
              </div>
              <span className="btn-label">{saving ? 'Saving…' : 'Save'}</span>
            </button>
          </div>
        </div>
      }
    >
      <BillStatusBody
        bill={bill}
        status={status}
        setStatus={setStatus}
        queried={queried}
        onQueriedChange={onQueriedChange}
        outcome={outcome}
        error={error}
        saving={saving}
      />
      <ConfirmDialog
        isOpen={confirmQuery}
        title="Query an approved bill?"
        message="This bill is approved in Xero. Marking it queried will void it in Xero and send it again as a Draft. Continue?"
        confirmText="Yes"
        cancelText="No"
        type="warning"
        onConfirm={() => { setConfirmQuery(false); tickQueried(); }}
        onCancel={() => setConfirmQuery(false)}
      />
      {priorMonth.dialog}
    </Modal>
  );
}

export default BillStatusDialog;
